import { useEffect, useRef } from 'react';

export const RELOAD_EVERY_MS = 6 * 60 * 60 * 1000;
const VERSION_CHECK_MS = 10 * 60 * 1000;

function scriptsOf(html: string): string {
  return (html.match(/\/static\/js\/main\.[^"']+\.js/g) ?? []).sort().join('|');
}

/**
 * O telão fica aberto o dia todo. Para não acumular nada que tenha escapado
 * (memória do navegador da TV) e para pegar uma versão nova do frontend,
 * recarrega a página — mas só quando não há animação rodando (`busy`).
 *
 * - A cada 6h.
 * - Quando o index.html publicado aponta para outro bundle (deploy novo).
 *
 * `enabled = false` desliga (demo: recarregar perderia o roteiro).
 */
export default function useSafetyReload(busy: boolean, enabled = true): void {
  const due = useRef(false);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    if (!enabled) return undefined;

    const markDue = () => {
      due.current = true;
      if (!busyRef.current) window.location.reload();
    };

    const reloadTimer = setTimeout(markDue, RELOAD_EVERY_MS);
    const current = scriptsOf(document.documentElement.outerHTML);

    const versionTimer = setInterval(async () => {
      try {
        const response = await fetch('/index.html', { cache: 'no-store' });
        const published = scriptsOf(await response.text());
        if (current && published && published !== current) markDue();
      } catch {
        // Sem rede: tenta na próxima.
      }
    }, VERSION_CHECK_MS);

    return () => {
      clearTimeout(reloadTimer);
      clearInterval(versionTimer);
    };
  }, [enabled]);

  useEffect(() => {
    if (due.current && !busy) window.location.reload();
  }, [busy]);
}
